import { useEffect, useState } from 'react';
import { Handshake, ArrowLeft, RefreshCw, Eye, Play, Pause, AlertCircle, CheckCircle2, Clock, TrendingUp, Layers, Wallet, DollarSign } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/super-admin`;

const fmtVES = (n) => `VES ${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const fmtSchedule = (h, m) => {
  if (h === null || h === undefined) return '22:15 UTC · 18:15 VE';
  const hh = String(h).padStart(2, '0');
  const mm = String(m ?? 0).padStart(2, '0');
  const veH = (Number(h) - 4 + 24) % 24;
  return `${hh}:${mm} UTC · ${String(veH).padStart(2, '0')}:${mm} VE`;
};

// weekday() de Python: lun=0 .. dom=6. Se muestran en orden Dom→Sáb.
const WEEKDAYS = [
  { v: 6, l: 'Dom' }, { v: 0, l: 'Lun' }, { v: 1, l: 'Mar' },
  { v: 2, l: 'Mié' }, { v: 3, l: 'Jue' }, { v: 4, l: 'Vie' }, { v: 5, l: 'Sáb' },
];
const fmtDays = (csv) => {
  if (csv === null || csv === undefined || String(csv).trim() === '') return 'Todos los días';
  const set = new Set(String(csv).split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n)));
  const labels = WEEKDAYS.filter((d) => set.has(d.v)).map((d) => d.l);
  return labels.length ? labels.join(', ') : 'Todos los días';
};

const StatusPill = ({ status }) => {
  const map = {
    success: { bg: 'bg-green-100', text: 'text-green-800', label: 'Exitosa' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Fallida' },
    unknown: { bg: 'bg-orange-100', text: 'text-orange-800', label: '⚠ Verificar (timeout)' },
    skipped_zero: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Sin comisión' },
    settled_manually: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Liquidada manual' },
  };
  const s = map[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status || '—' };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
};

const SweepPill = ({ status }) => {
  const map = {
    success: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Barrido OK' },
    sweeping: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Barriendo…' },
    pending_retry: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Reintentando' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Barrido falló' },
    skipped_zero: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Sin saldo' },
  };
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const s = map[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
};

// Panel de la concentradora COMPARTIDA de Caura: muestra el saldo interno
// acumulado por cada comercio (flujo caura_to_merchant) para que Ragu y Kompa
// queden claramente separados aunque usen la misma cuenta física.
const ConcentratorPanel = ({ concentrator }) => {
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/alliance-settlements/reconciliations?limit=30`, { headers: headers() });
      setHistory(res.data?.reconciliations || []);
    } catch (e) {
      setHistory([]);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const runReconcile = async () => {
    setReconLoading(true);
    try {
      const res = await axios.get(`${API}/alliance-settlements/concentrator-balance`, { headers: headers() });
      setRecon(res.data);
      loadHistory();
    } catch (e) {
      setRecon({ bank_error: e.response?.data?.detail || 'Error consultando banco' });
    } finally {
      setReconLoading(false);
    }
  };

  if (!concentrator || !concentrator.merchants || concentrator.merchants.length === 0) return null;
  const rows = concentrator.merchants;
  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500', 'bg-pink-500'];
  const combined = Number(concentrator.combined_internal_balance || 0);
  return (
    <div className="bg-white border-2 border-slate-200 rounded-xl p-5 mb-6" data-testid="concentrator-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-blue-600" />
          <h3 className="font-bold text-gray-900">Concentradora Compartida Caura</h3>
          {concentrator.shared && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 uppercase tracking-wide">Compartida</span>
          )}
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-gray-500">Saldo wallets internas (no es el ledger)</div>
          <div className="text-lg font-bold text-gray-900" data-testid="concentrator-combined-balance">{fmtVES(combined)}</div>
        </div>
      </div>

      {/* Barra de distribución por comercio */}
      {combined > 0 && (
        <div className="flex w-full h-3 rounded-full overflow-hidden mb-4 bg-gray-100">
          {rows.map((r, i) => {
            const pct = combined > 0 ? (Number(r.internal_balance || 0) / combined) * 100 : 0;
            return <div key={r.merchant_id} className={colors[i % colors.length]} style={{ width: `${pct}%` }} title={`${r.company_name}: ${pct.toFixed(1)}%`} />;
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r, i) => (
          <div key={r.merchant_id} className="flex items-center justify-between border border-gray-200 rounded-lg p-3" data-testid={`concentrator-row-${r.email}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-3 h-3 rounded-full ${colors[i % colors.length]} shrink-0`} />
              <div className="min-w-0">
                <div className="font-semibold text-sm text-gray-900 truncate">{r.company_name}</div>
                <div className="text-[11px] text-gray-500 font-mono truncate">{r.email}</div>
              </div>
            </div>
            <div className="text-right shrink-0 ml-3">
              <div className="font-bold text-gray-900 text-sm" data-testid={`concentrator-balance-${r.email}`}>{fmtVES(r.internal_balance)}</div>
              <div className="text-[11px] text-gray-500">Liquidado: {fmtVES(r.total_settled_out)}</div>
              {Number(r.pending_or_failed_commission) > 0 && (
                <div className="text-[11px] text-amber-700">Pend.: {fmtVES(r.pending_or_failed_commission)}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Conciliación banco vs ledger */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">Conciliación banco vs ledger</span>
          <button onClick={runReconcile} disabled={reconLoading} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5" data-testid="reconcile-concentrator-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${reconLoading ? 'animate-spin' : ''}`} /> {reconLoading ? 'Consultando banco…' : 'Conciliar con banco'}
          </button>
        </div>
        {recon && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="reconcile-result">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[11px] text-gray-500">Saldo físico (banco)</div>
              <div className="font-bold text-gray-900">{recon.physical_balance != null ? fmtVES(recon.physical_balance) : '—'}</div>
              <div className="text-[10px] text-gray-400 font-mono">…{(recon.account || '').slice(-4)}</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[11px] text-gray-500">Saldo wallets internas</div>
              <div className="font-bold text-gray-900">{fmtVES(recon.combined_internal_balance)}</div>
            </div>
            <div className={`rounded-lg border p-3 ${recon.discrepancy == null ? 'border-gray-200' : Math.abs(recon.discrepancy) < 0.01 ? 'border-emerald-300 bg-emerald-50' : recon.discrepancy > 0 ? 'border-blue-300 bg-blue-50' : 'border-red-300 bg-red-50'}`}>
              <div className="text-[11px] text-gray-500">Diferencia (banco − ledger)</div>
              <div className={`font-bold ${recon.discrepancy == null ? 'text-gray-400' : Math.abs(recon.discrepancy) < 0.01 ? 'text-emerald-700' : recon.discrepancy > 0 ? 'text-blue-700' : 'text-red-700'}`} data-testid="reconcile-discrepancy">
                {recon.discrepancy != null ? fmtVES(recon.discrepancy) : '—'}
              </div>
              {recon.discrepancy != null && (
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {Math.abs(recon.discrepancy) < 0.01 ? 'Cuadrado ✓' : recon.discrepancy > 0 ? 'Banco tiene de más (comisiones/otros)' : 'Banco tiene MENOS que lo debido ⚠️'}
                </div>
              )}
            </div>
            {recon.bank_error && (
              <div className="sm:col-span-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Banco no respondió: {recon.bank_error}. La conciliación requiere conexión a Banco Plaza (puede fallar en Preview; reintenta en Producción).
              </div>
            )}
          </div>
        )}
      </div>

      {/* Historial de conciliaciones automáticas */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <button
          onClick={() => setHistoryOpen(o => !o)}
          className="flex items-center justify-between w-full text-left"
          data-testid="reconcile-history-toggle"
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Historial de conciliaciones {history.length > 0 && `(${history.length})`}
          </span>
          <span className="text-xs text-blue-600">{historyOpen ? 'Ocultar' : 'Ver'}</span>
        </button>
        {historyOpen && (
          <div className="mt-3 overflow-x-auto" data-testid="reconcile-history-list">
            {history.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">Aún no hay conciliaciones registradas. Se generan automáticamente tras cada settlement.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-100">
                    <th className="text-left py-2 font-medium">Fecha</th>
                    <th className="text-left py-2 font-medium">Origen</th>
                    <th className="text-right py-2 font-medium">Banco</th>
                    <th className="text-right py-2 font-medium">Ledger</th>
                    <th className="text-right py-2 font-medium">Diferencia</th>
                    <th className="text-center py-2 font-medium">Alerta</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => {
                    const d = h.discrepancy;
                    const diffColor = d == null ? 'text-gray-400' : Math.abs(d) < 0.01 ? 'text-emerald-700' : d > 0 ? 'text-blue-700' : 'text-red-700';
                    return (
                      <tr key={h.reconciliation_id} className="border-b border-gray-50" data-testid={`reconcile-history-row-${h.reconciliation_id}`}>
                        <td className="py-2 text-gray-700">{fmtDate(h.checked_at)}</td>
                        <td className="py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${h.trigger_type === 'post_settlement' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                            {h.trigger_type === 'post_settlement' ? 'Auto' : 'Manual'}
                          </span>
                        </td>
                        <td className="py-2 text-right text-gray-700">{h.physical_balance != null ? fmtVES(h.physical_balance) : (h.bank_error ? '⚠️' : '—')}</td>
                        <td className="py-2 text-right text-gray-700">{fmtVES(h.combined_internal_balance)}</td>
                        <td className={`py-2 text-right font-semibold ${diffColor}`}>{d != null ? fmtVES(d) : '—'}</td>
                        <td className="py-2 text-center">
                          {h.alert_triggered ? (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700" title={`Alerta email: ${h.alert_status || 'enviada'}`}>Alerta ⚠️</span>
                          ) : (
                            <span className="text-emerald-600 text-[11px]">OK</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-400 mt-3">
        Saldo interno = lo que Caura retiene/debe a cada comercio en su ledger (separado por comercio aunque la cuenta bancaria sea la misma).
      </p>
    </div>
  );
};

const KpiCard = ({ label, value, sub, color = 'blue', icon: Icon, testId }) => {
  const colors = {
    blue: 'border-blue-200 bg-blue-50', green: 'border-green-200 bg-green-50',
    amber: 'border-amber-200 bg-amber-50', red: 'border-red-200 bg-red-50',
    purple: 'border-purple-200 bg-purple-50',
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${colors[color]}`} data-testid={testId || `kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{label}</span>
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
      </div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
};

export default function AlliancesTab({ setToast }) {
  const [merchants, setMerchants] = useState([]);
  const [concentrator, setConcentrator] = useState(null);
  const [selected, setSelected] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [showZero, setShowZero] = useState(false);
  const [zeroCount, setZeroCount] = useState(0);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [configOpen, setConfigOpen] = useState(null); // null | { create:true } | { merchant }
  const [runModal, setRunModal] = useState(null); // merchant del corte manual
  const [splitModal, setSplitModal] = useState(null); // merchant del reparto manual

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const loadMerchants = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/alliance-settlements/merchants`, { headers: headers() });
      setMerchants(res.data.merchants || []);
      setConcentrator(res.data.concentrator || null);
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error cargando alianzas' });
    } finally {
      setLoading(false);
    }
  };

  const loadSettlements = async (merchantId, includeZero = showZero) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/alliance-settlements?merchant_id=${merchantId}&limit=200&include_zero=${includeZero}`, { headers: headers() });
      setSettlements(res.data.settlements || []);
      setZeroCount(res.data.zero_count || 0);
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error cargando historial' });
    } finally {
      setLoading(false);
    }
  };

  const toggleZero = () => {
    const next = !showZero;
    setShowZero(next);
    if (selected) loadSettlements(selected.user_id, next);
  };

  // Auto-refresco mientras haya barridos en "Reintentando" (pending_retry): el
  // job de reintentos corre en segundo plano (cada ~5 min) y puede completarlos
  // solo (p.ej. tras un DNS intermitente). Sin esto la UI se queda "pegada" en
  // Reintentando aunque el barrido ya haya sido exitoso en el banco.
  useEffect(() => {
    if (!selected) return;
    const hasPending = (settlements || []).some((s) => s.sweep_status === 'pending_retry' || s.sweep_status === 'sweeping');
    if (!hasPending) return;
    const t = setInterval(() => { loadSettlements(selected.user_id); }, 20000);
    return () => clearInterval(t);
  }, [selected, settlements]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/alliance-settlements/merchants`, { headers: headers() });
        if (!active) return;
        setMerchants(res.data.merchants || []);
        setConcentrator(res.data.concentrator || null);
      } catch (e) {
        if (active) setToast?.({ type: 'error', message: 'Error cargando alianzas' });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleRunNow = (merchantId) => {
    const m = merchants.find((x) => x.user_id === merchantId) || selected;
    setRunModal(m || { user_id: merchantId });
  };

  const handleRetry = async (settlementId) => {
    if (!window.confirm('Reintentar transferencia bancaria para este settlement?\n\nNo se recalculan los montos, solo se reintenta el CCE.')) return;
    try {
      const res = await axios.post(`${API}/alliance-settlements/${settlementId}/retry-transfer`, {}, { headers: headers() });
      if (res.data.ok) {
        setToast?.({ type: 'success', message: `Transferencia exitosa: ref ${res.data.bank_reference || '-'}` });
      } else {
        setToast?.({ type: 'error', message: `Falló: ${res.data.error || 'banco rechazó'}` });
      }
      await loadSettlements(selected.user_id);
      await loadMerchants();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error reintentando' });
    }
  };

  const handleRetrySweep = async (settlementId) => {
    if (!window.confirm('Reintentar el barrido del saldo restante a la cuenta administrativa?\n\nConsulta el saldo real disponible y lo transfiere.')) return;
    try {
      const res = await axios.post(`${API}/alliance-settlements/${settlementId}/retry-sweep`, {}, { headers: headers() });
      const sw = res.data.sweep || {};
      if (res.data.ok) {
        setToast?.({ type: 'success', message: `Barrido exitoso: VES ${Number(sw.swept || 0).toFixed(2)} | ref ${sw.reference || '-'}` });
      } else if (sw.skipped) {
        setToast?.({ type: 'success', message: `Sin saldo que barrer (VES ${Number(sw.balance || 0).toFixed(2)})` });
      } else {
        setToast?.({ type: 'error', message: `Barrido falló: ${sw.error || 'banco no respondió'}` });
      }
      await loadSettlements(selected.user_id);
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error reintentando barrido' });
    }
  };

  // Resuelve un corte en estado 'unknown' (timeout ambiguo del CCE de comisión).
  const handleResolve = async (settlementId) => {
    const salio = window.confirm(
      '⚠ Este corte quedó PENDIENTE DE VERIFICACIÓN por timeout del banco.\n\n' +
      'Revisa el estado de cuenta bancario:\n' +
      '• Aceptar = la comisión SÍ salió (te pediré la referencia bancaria).\n' +
      '• Cancelar = la comisión NO salió (se marcará como fallida y podrás reintentar).'
    );
    try {
      if (salio) {
        const ref = window.prompt('Ingresa el número de referencia bancaria de la comisión que SÍ salió:');
        if (ref === null) return;
        if (!ref.trim()) { setToast?.({ type: 'error', message: 'La referencia es obligatoria.' }); return; }
        const res = await axios.post(`${API}/alliance-settlements/${settlementId}/resolve`,
          { outcome: 'success', bank_reference: ref.trim() }, { headers: headers() });
        const sw = res.data?.sweep || {};
        setToast?.({ type: 'success', message: `Resuelto como Exitoso. Comisión ${Number(res.data?.commission || 0).toFixed(2)} registrada. Barrido: ${sw.ok ? `✓ ${Number(sw.swept || 0).toFixed(2)}` : 'pendiente (reintentar)'}` });
      } else {
        const res = await axios.post(`${API}/alliance-settlements/${settlementId}/resolve`,
          { outcome: 'failed' }, { headers: headers() });
        if (res.data?.ok) setToast?.({ type: 'success', message: 'Resuelto como Fallido. Ya puedes reintentar o correr un nuevo corte.' });
      }
      await loadSettlements(selected.user_id);
      await loadMerchants();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error resolviendo el corte' });
    }
  };

  const handleTogglePause = async (merchantId, nextPaused) => {
    const word = nextPaused ? 'PAUSAR' : 'REANUDAR';
    if (!window.confirm(`¿${word} el settlement automático (8am/corte diario) de este comercio?\n\nLas corridas manuales "Ejecutar ahora" siguen disponibles.`)) return;
    try {
      await axios.put(`${API}/merchants/${merchantId}/alliance-pause`, { paused: nextPaused }, { headers: headers() });
      setToast?.({ type: 'success', message: nextPaused ? 'Settlement automático pausado' : 'Settlement automático reanudado' });
      await loadMerchants();
      if (selected && selected.user_id === merchantId) {
        setSelected({ ...selected, alliance_settlement_paused: nextPaused });
      }
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error cambiando estado' });
    }
  };

  const handleToggleCommissionSplit = async (merchantId, nextPaused) => {
    const word = nextPaused ? 'APAGAR' : 'ACTIVAR';
    if (!window.confirm(`¿${word} el reparto automático de la comisión de este comercio?\n\nApagado: la comisión se queda en la cuenta de comisiones de Caura y la reparas manualmente.`)) return;
    try {
      await axios.put(`${API}/merchants/${merchantId}/commission-split-pause`, { paused: nextPaused }, { headers: headers() });
      setToast?.({ type: 'success', message: nextPaused ? 'Reparto de comisiones apagado' : 'Reparto de comisiones activado' });
      await loadMerchants();
      if (selected && selected.user_id === merchantId) {
        setSelected({ ...selected, commission_split_paused: nextPaused });
      }
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error cambiando estado' });
    }
  };

  const openDetail = async (settlementId) => {
    try {
      const res = await axios.get(`${API}/alliance-settlements/${settlementId}`, { headers: headers() });
      setDetail(res.data);
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error cargando detalle' });
    }
  };

  // ── List view ────────────────────────────────────────────────
  if (!selected) {
    return (
      <div data-testid="alliances-tab">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#051C2C' }}>
              <Handshake className="w-7 h-7 text-purple-600" />
              Alianzas Comerciales
            </h2>
            <p className="text-sm text-gray-500 mt-1">Merchants con modelo de comisión diaria (alianza), no per-transacción</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setConfigOpen({ create: true })} className="px-3 py-2 text-sm rounded-lg bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-2" data-testid="new-alliance-merchant">
              + Nueva alianza
            </button>
            <button onClick={loadMerchants} className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 flex items-center gap-2" data-testid="refresh-alliances">
              <RefreshCw className="w-4 h-4" /> Refrescar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando...</div>
        ) : merchants.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <Handshake className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold">No hay merchants en modelo de alianza</p>
            <p className="text-sm text-gray-500 mt-2">Configura uno con PUT /api/super-admin/merchants/{`{id}`}/commission-model</p>
          </div>
        ) : (
          <>
            <ConcentratorPanel concentrator={concentrator} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {merchants.map((m) => (
              <div key={m.user_id} className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-purple-300 transition cursor-pointer" onClick={() => { setSelected(m); loadSettlements(m.user_id); }} data-testid={`alliance-card-${m.email}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{m.company_name || m.email}</h3>
                    <p className="text-xs text-gray-500 font-mono">{m.email}</p>
                    <p className="text-xs text-gray-400 mt-1">RIF {m.alliance_bank_rif} · Cuenta …{(m.alliance_bank_account || '').slice(-4)}</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">{m.alliance_daily_rate_pct}% diario</span>
                </div>
                {m.alliance_settlement_paused && (
                  <div className="mb-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800" data-testid={`paused-badge-${m.email}`}>
                    <Pause className="w-3.5 h-3.5" /> Auto-settlement pausado
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">Ingresos totales</div>
                    <div className="font-bold text-gray-900">{fmtVES(m.kpis?.total_gross_income)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Comisiones cobradas</div>
                    <div className="font-bold text-green-700">{fmtVES(m.kpis?.total_commission_collected)}</div>
                  </div>
                  <div className="flex items-start gap-1">
                    <Wallet className="w-3.5 h-3.5 text-blue-500 mt-0.5" />
                    <div>
                      <div className="text-xs text-gray-500">Saldo del ledger</div>
                      <div className="font-bold text-blue-700" data-testid={`card-ledger-balance-${m.email}`}>{fmtVES(m.ledger_balance)}</div>
                      <div className="text-[11px] text-gray-400" data-testid={`card-internal-balance-${m.email}`}>Wallets internas: {fmtVES(m.internal_balance)}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Corte diario</div>
                    <div className="font-semibold text-purple-700 text-xs">{fmtSchedule(m.alliance_settlement_hour_utc, m.alliance_settlement_minute_utc)}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5" data-testid={`card-days-${m.email}`}>{fmtDays(m.alliance_settlement_weekdays)}{m.alliance_settlement_full_balance ? ' · barre todo' : ''}</div>
                  </div>
                </div>
                {m.kpis?.last_settlement_at && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>Último settlement: {fmtDate(m.kpis.last_settlement_at)}</span>
                    <StatusPill status={m.kpis.last_settlement_status} />
                  </div>
                )}
              </div>
            ))}
            </div>
          </>
        )}
        {configOpen && (
          <AllianceConfigModal
            merchant={configOpen.merchant || null}
            isCreate={!!configOpen.create}
            onClose={() => setConfigOpen(null)}
            onSaved={async () => {
              setConfigOpen(null);
              await loadMerchants();
              setToast?.({ type: 'success', message: 'Alianza guardada' });
            }}
            setToast={setToast}
          />
        )}
      </div>
    );
  }

  // ── Detail view ──────────────────────────────────────────────
  const k = selected.kpis || {};
  return (
    <div data-testid="alliances-detail">
      <button onClick={() => { setSelected(null); setSettlements([]); }} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4" data-testid="back-to-alliances">
        <ArrowLeft className="w-4 h-4" /> Volver a alianzas
      </button>

      {/* Header */}
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-5 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{selected.company_name || selected.email}</h2>
            <p className="text-sm text-gray-600 font-mono mt-1">{selected.email}</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs text-gray-700">
              <div><span className="text-gray-500">Cuenta origen:</span> <span className="font-mono font-semibold">{selected.alliance_bank_account}</span></div>
              <div><span className="text-gray-500">Banco:</span> <span className="font-semibold">{selected.alliance_bank_name} ({selected.alliance_bank_code})</span></div>
              <div><span className="text-gray-500">RIF:</span> <span className="font-mono font-semibold">{selected.alliance_bank_rif}</span></div>
              <div><span className="text-gray-500">Tasa:</span> <span className="font-bold text-purple-700">{selected.alliance_daily_rate_pct}% diario sobre {selected.alliance_flow_direction === 'caura_to_merchant' ? 'bruto' : 'neto'}</span></div>
              <div className="col-span-2"><span className="text-gray-500">Horario de corte:</span> <span className="font-semibold">{fmtSchedule(selected.alliance_settlement_hour_utc, selected.alliance_settlement_minute_utc)}</span></div>
              <div className="col-span-2"><span className="text-gray-500">Días de corte:</span> <span className="font-semibold" data-testid="detail-corte-days">{fmtDays(selected.alliance_settlement_weekdays)}</span>{selected.alliance_settlement_full_balance && <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-semibold">Barre todo el saldo acumulado</span>}</div>
              <div className="col-span-2"><span className="text-gray-500">Flujo:</span> <span className="font-semibold">{selected.alliance_flow_direction === 'caura_to_merchant' ? 'Caura → Merchant (Caura paga el neto)' : 'Merchant → Caura (Caura debita la comisión)'}</span></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => handleTogglePause(selected.user_id, !selected.alliance_settlement_paused)} className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 border ${selected.alliance_settlement_paused ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'bg-white border-orange-300 text-orange-700 hover:bg-orange-50'}`} data-testid="toggle-pause-btn">
              {selected.alliance_settlement_paused ? <><Play className="w-4 h-4" /> Reanudar auto</> : <><Pause className="w-4 h-4" /> Pausar auto</>}
            </button>
            {selected.commission_split_configured && (
              <button onClick={() => handleToggleCommissionSplit(selected.user_id, !selected.commission_split_paused)} className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2 border ${selected.commission_split_paused ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' : 'bg-white border-orange-300 text-orange-700 hover:bg-orange-50'}`} data-testid="toggle-commission-split-btn">
                {selected.commission_split_paused ? <><Play className="w-4 h-4" /> Activar reparto comisión</> : <><Pause className="w-4 h-4" /> Apagar reparto comisión</>}
              </button>
            )}
            <button onClick={() => setConfigOpen({ merchant: selected })} className="px-3 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50" data-testid="edit-alliance-config">
              Editar configuración
            </button>
            <button onClick={() => handleRunNow(selected.user_id)} disabled={running} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2" data-testid="run-settlement-now">
              <Play className="w-4 h-4" /> {running ? 'Ejecutando...' : 'Ejecutar settlement ahora'}
            </button>
            <button onClick={() => setSplitModal(selected)} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 flex items-center gap-2" data-testid="run-commission-split">
              <DollarSign className="w-4 h-4" /> Repartir comisión ahora
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Settlements" value={k.settlements_count || 0} sub="Total registrados" color="blue" icon={Clock} />
        <KpiCard label="Ingresos brutos" value={fmtVES(k.total_gross_income)} sub="Suma histórica" color="purple" icon={TrendingUp} />
        <KpiCard label="Comisión cobrada" value={fmtVES(k.total_commission_collected)} sub="Solo transferencias exitosas" color="green" icon={CheckCircle2} />
        <KpiCard label="Saldo del ledger" value={fmtVES(selected.ledger_balance)} sub="Ledger inmutable — base del corte" color="blue" icon={Wallet} testId="kpi-ledger-balance" />
        <KpiCard label="Saldo wallets internas" value={fmtVES(selected.internal_balance)} sub="Suma de wallets main (contabilidad paralela, NO es el ledger)" color="amber" icon={Wallet} testId="kpi-internal-balance" />
        <KpiCard label="Total liquidado" value={fmtVES(selected.total_settled_out)} sub="CCE exitosos a/desde este comercio" color="purple" icon={TrendingUp} />
        <KpiCard label="Pendiente / fallida" value={fmtVES(k.pending_or_failed_commission)} sub="Requieren retry" color={k.pending_or_failed_commission > 0 ? 'amber' : 'blue'} icon={AlertCircle} />
        <KpiCard label="Comisiones bancarias" value={fmtVES(k.total_bank_commissions)} sub="Fees del banco históricos" color="red" />
        <KpiCard label="Refunds históricos" value={fmtVES(k.total_refunds)} sub="Montos refundeados" color="amber" />
        <KpiCard label="Último settlement" value={fmtDate(k.last_settlement_at)} sub={<StatusPill status={k.last_settlement_status} />} color="blue" />
      </div>

      {/* Settlements table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between gap-3">
          <h3 className="font-bold text-gray-800">Historial de settlements ({settlements.length})</h3>
          <button
            type="button"
            onClick={toggleZero}
            data-testid="toggle-zero-settlements-btn"
            className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-white hover:border-gray-400 transition-colors"
          >
            {showZero
              ? 'Ocultar cortes en cero'
              : `Ver cortes en cero${zeroCount ? ` (${zeroCount})` : ''}`}
          </button>
        </div>
        {!showZero && zeroCount > 0 && (
          <div className="px-5 py-2 text-xs text-gray-500 bg-amber-50 border-b" data-testid="zero-settlements-note">
            {zeroCount} corrida(s) sin movimientos ocultas para no enterrar los cortes reales. Siguen guardadas en la auditoría.
          </div>
        )}
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : settlements.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Sin settlements registrados todavía. El primer settlement correrá hoy a las 6 PM VE.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Fecha</th>
                  <th className="px-4 py-3 text-right">Gross</th>
                  <th className="px-4 py-3 text-right">Fees banco</th>
                  <th className="px-4 py-3 text-right">Refunds</th>
                  <th className="px-4 py-3 text-right">Net</th>
                  <th className="px-4 py-3 text-right">Comisión {selected.alliance_daily_rate_pct}%</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Ref bancaria</th>
                  {selected.alliance_sweep_account && <th className="px-4 py-3 text-center">Barrido</th>}
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.settlement_id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-700">{fmtDate(s.created_at)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtVES(s.gross_income)}</td>
                    <td className="px-4 py-3 text-right font-mono text-red-700">-{fmtVES(s.bank_commissions)}</td>
                    <td className="px-4 py-3 text-right font-mono text-amber-700">-{fmtVES(s.refunds)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{fmtVES(s.net_income)}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-purple-700">{fmtVES(s.commission_amount)}</td>
                    <td className="px-4 py-3 text-center"><StatusPill status={s.bank_transfer_status} /></td>
                    <td className="px-4 py-3 text-center text-xs font-mono text-gray-600">{s.bank_transfer_reference || '—'}</td>
                    {selected.alliance_sweep_account && (
                      <td className="px-4 py-3 text-center" data-testid={`sweep-cell-${s.settlement_id}`}>
                        <div className="flex flex-col items-center gap-0.5">
                          <SweepPill status={s.sweep_status} />
                          {s.sweep_status === 'success' && Number(s.sweep_amount) > 0 && (
                            <span className="text-[10px] font-mono text-emerald-700">{fmtVES(s.sweep_amount)}</span>
                          )}
                          {s.sweep_status === 'success' && s.sweep_reference && (
                            <span className="text-[10px] font-mono text-gray-500" data-testid={`sweep-ref-${s.settlement_id}`}>ref {s.sweep_reference}</span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openDetail(s.settlement_id)} className="p-1.5 hover:bg-blue-100 rounded text-blue-700" title="Ver detalle" data-testid={`view-detail-${s.settlement_id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                        {(s.bank_transfer_status === 'failed' || s.bank_transfer_status === 'pending') && Number(s.commission_amount) > 0 && (
                          <button onClick={() => handleRetry(s.settlement_id)} className="p-1.5 hover:bg-amber-100 rounded text-amber-700" title="Cobrar comisión (reintentar CCE a Caura Comisiones)" data-testid={`retry-${s.settlement_id}`}>
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        {s.bank_transfer_status === 'unknown' && (
                          <button onClick={() => handleResolve(s.settlement_id)} className="p-1.5 hover:bg-orange-100 rounded text-orange-700" title="Resolver corte (verificar en banco)" data-testid={`resolve-${s.settlement_id}`}>
                            <AlertCircle className="w-4 h-4" />
                          </button>
                        )}
                        {selected.alliance_sweep_account && (s.sweep_status === 'pending_retry' || s.sweep_status === 'failed') && (
                          <button onClick={() => handleRetrySweep(s.settlement_id)} className="p-1.5 hover:bg-emerald-100 rounded text-emerald-700" title="Reintentar barrido" data-testid={`retry-sweep-${s.settlement_id}`}>
                            <TrendingUp className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="font-bold text-lg">Detalle de settlement</h3>
              <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Settlement ID:</span> <span className="font-mono text-xs">{detail.settlement?.settlement_id}</span></div>
                <div><span className="text-gray-500">Ejecutado por:</span> <span className="font-semibold">{detail.settlement?.executed_by}</span></div>
                <div><span className="text-gray-500">Ventana:</span> <span>{fmtDate(detail.settlement?.window_start)} → {fmtDate(detail.settlement?.window_end)}</span></div>
                <div><span className="text-gray-500">Status:</span> <StatusPill status={detail.settlement?.bank_transfer_status} /></div>
                <div><span className="text-gray-500">Ref banco:</span> <span className="font-mono">{detail.settlement?.bank_transfer_reference || '—'}</span></div>
                <div><span className="text-gray-500">Wallet ajustado:</span> <span>{detail.settlement?.wallet_adjusted ? '✓ Sí' : '✗ No'}</span></div>
              </div>

              {detail.settlement?.bank_transfer_error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                  <strong>Error CCE:</strong> {detail.settlement.bank_transfer_error}
                </div>
              )}

              {detail.settlement?.sweep_status && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-600" /> Barrido de saldo restante
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-500">Estado:</span> <SweepPill status={detail.settlement.sweep_status} /></div>
                    <div><span className="text-gray-500">Monto barrido:</span> <span className="font-mono font-semibold">{fmtVES(detail.settlement.sweep_amount)}</span></div>
                    <div><span className="text-gray-500">Ref barrido:</span> <span className="font-mono">{detail.settlement.sweep_reference || '—'}</span></div>
                    <div><span className="text-gray-500">Ejecutado:</span> <span>{fmtDate(detail.settlement.sweep_executed_at)}</span></div>
                  </div>
                  {detail.settlement.sweep_error && detail.settlement.sweep_status !== 'success' && (
                    <div className="mt-2 text-xs text-red-700"><strong>Error barrido:</strong> {detail.settlement.sweep_error}</div>
                  )}
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-sm mb-3">Cálculo</h4>
                <div className="space-y-1 text-sm font-mono">
                  <div className="flex justify-between"><span>Gross income:</span> <span>{fmtVES(detail.settlement?.gross_income)}</span></div>
                  <div className="flex justify-between text-red-700"><span>− Comisiones bancarias:</span> <span>-{fmtVES(detail.settlement?.bank_commissions)}</span></div>
                  <div className="flex justify-between text-amber-700"><span>− Refunds:</span> <span>-{fmtVES(detail.settlement?.refunds)}</span></div>
                  <div className="flex justify-between border-t pt-1 font-bold"><span>= Net income:</span> <span>{fmtVES(detail.settlement?.net_income)}</span></div>
                  <div className="flex justify-between text-purple-700 font-bold"><span>× {detail.settlement?.commission_rate_pct}%:</span> <span>{fmtVES(detail.settlement?.commission_amount)}</span></div>
                </div>
              </div>

              {detail.settlement?.movements_snapshot?.debits?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Movimientos bancarios (débitos) — {detail.settlement.movements_snapshot.debits_count}</h4>
                  <div className="max-h-48 overflow-y-auto border rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr><th className="px-2 py-1 text-left">Fecha/Hora</th><th className="text-right">Monto</th><th className="text-left">Ref</th><th className="text-left">Descripción</th></tr>
                      </thead>
                      <tbody>
                        {detail.settlement.movements_snapshot.debits.map((d, i) => (
                          <tr key={i} className="border-b">
                            <td className="px-2 py-1">{d.fecha} {d.hora}</td>
                            <td className="text-right font-mono">{fmtVES(d.monto)}</td>
                            <td className="font-mono text-xs">{d.referencia}</td>
                            <td className="text-gray-600">{d.descripcion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Config modal: aplica en list & detail views */}
      {configOpen && (
        <AllianceConfigModal
          merchant={configOpen.merchant || null}
          isCreate={!!configOpen.create}
          onClose={() => setConfigOpen(null)}
          onSaved={async () => {
            setConfigOpen(null);
            await loadMerchants();
            if (selected) {
              try {
                const refreshed = (await axios.get(`${API}/alliance-settlements/merchants`, { headers: headers() })).data.merchants || [];
                const sel = refreshed.find(x => x.user_id === selected.user_id);
                if (sel) setSelected(sel);
              } catch (e) { /* noop */ }
            }
            setToast?.({ type: 'success', message: 'Alianza guardada' });
          }}
          setToast={setToast}
        />
      )}
      {runModal && (
        <RunSettlementModal
          merchant={runModal}
          onClose={() => setRunModal(null)}
          onDone={async () => {
            await loadMerchants();
            if (selected) await loadSettlements(selected.user_id);
          }}
        />
      )}
      {splitModal && (
        <CommissionSplitModal merchant={splitModal} onClose={() => setSplitModal(null)} />
      )}
    </div>
  );
}
function AllianceConfigModal({ merchant, isCreate, onClose, onSaved, setToast }) {
  const [email, setEmail] = useState('');
  const [merchantId, setMerchantId] = useState(merchant?.user_id || '');
  const [ratePct, setRatePct] = useState(merchant?.alliance_daily_rate_pct ?? 1.0);
  const [hourUtc, setHourUtc] = useState(merchant?.alliance_settlement_hour_utc ?? 12);
  const [minuteUtc, setMinuteUtc] = useState(merchant?.alliance_settlement_minute_utc ?? 0);
  const [flowDirection, setFlowDirection] = useState(merchant?.alliance_flow_direction || 'caura_to_merchant');
  const [weekdays, setWeekdays] = useState(() => {
    const raw = merchant?.alliance_settlement_weekdays;
    if (raw === null || raw === undefined || String(raw).trim() === '') return new Set();
    return new Set(String(raw).split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n)));
  });
  const [fullBalance, setFullBalance] = useState(!!merchant?.alliance_settlement_full_balance);
  const [bankAccount, setBankAccount] = useState(merchant?.alliance_bank_account || '');
  const [bankCode, setBankCode] = useState(merchant?.alliance_bank_code || '0138');
  const [bankRif, setBankRif] = useState(merchant?.alliance_bank_rif || '');
  const [bankName, setBankName] = useState(merchant?.alliance_bank_name || '');
  const [saving, setSaving] = useState(false);

  const token = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const lookupMerchant = async () => {
    if (!email) return;
    try {
      const res = await axios.get(`${API}/users?search=${encodeURIComponent(email)}&limit=5`, { headers: headers() });
      const list = res.data.users || res.data || [];
      const found = list.find(u => (u.email || '').toLowerCase() === email.toLowerCase());
      if (found) {
        setMerchantId(found.user_id);
        if (!bankName) setBankName((found.company_name || '').toUpperCase().slice(0, 80));
        setToast?.({ type: 'success', message: `Merchant encontrado: ${found.user_id}` });
      } else {
        setToast?.({ type: 'error', message: 'No se encontró merchant con ese email' });
      }
    } catch (e) {
      setToast?.({ type: 'error', message: 'Error buscando merchant' });
    }
  };

  const handleSave = async () => {
    if (!merchantId) {
      setToast?.({ type: 'error', message: 'Selecciona el merchant primero' });
      return;
    }
    if (!bankAccount || !bankRif) {
      setToast?.({ type: 'error', message: 'Cuenta y RIF son obligatorios' });
      return;
    }
    const r = parseFloat(ratePct);
    if (isNaN(r) || r < 0 || r > 100) {
      setToast?.({ type: 'error', message: 'Tasa debe ser 0-100' });
      return;
    }
    const h = parseInt(hourUtc, 10);
    const mi = parseInt(minuteUtc, 10);
    if (isNaN(h) || h < 0 || h > 23 || isNaN(mi) || mi < 0 || mi > 59) {
      setToast?.({ type: 'error', message: 'Hora UTC inválida' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/merchants/${merchantId}/commission-model`, {
        commission_model: 'daily_alliance',
        alliance_daily_rate_pct: r,
        alliance_settlement_hour_utc: h,
        alliance_settlement_minute_utc: mi,
        alliance_flow_direction: flowDirection,
        alliance_settlement_weekdays: weekdays.size ? Array.from(weekdays).sort((a, b) => a - b).join(',') : null,
        alliance_settlement_full_balance: fullBalance,
        alliance_bank_account: bankAccount.trim(),
        alliance_bank_code: bankCode.trim(),
        alliance_bank_rif: bankRif.trim(),
        alliance_bank_name: bankName.trim(),
      }, { headers: headers() });
      await onSaved();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || 'Error guardando' });
    } finally {
      setSaving(false);
    }
  };

  const veH = (Number(hourUtc) - 4 + 24) % 24;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="alliance-config-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="font-bold text-lg">{isCreate ? 'Nueva alianza comercial' : `Editar alianza · ${merchant?.email}`}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-6 space-y-5">
          {isCreate && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email del merchant existente</label>
              <div className="flex gap-2">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ragu@example.com" className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono" data-testid="alliance-email-input" />
                <button onClick={lookupMerchant} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="alliance-lookup-btn">Buscar</button>
              </div>
              {merchantId && <p className="text-xs text-green-700 mt-1 font-mono">user_id: {merchantId}</p>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Tasa de comisión (%)</label>
              <input type="number" step="0.01" min="0" max="100" value={ratePct} onChange={(e) => setRatePct(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="alliance-rate-input" />
              <p className="text-xs text-gray-500 mt-1">% sobre (gross − fees banco − refunds)</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Hora UTC</label>
                <input type="number" min="0" max="23" value={hourUtc} onChange={(e) => setHourUtc(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="alliance-hour-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Minuto</label>
                <input type="number" min="0" max="59" value={minuteUtc} onChange={(e) => setMinuteUtc(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="alliance-minute-input" />
              </div>
              <p className="col-span-2 text-xs text-purple-700 font-semibold mt-1">
                Corte: {String(hourUtc).padStart(2, '0')}:{String(minuteUtc).padStart(2, '0')} UTC = {String(veH).padStart(2, '0')}:{String(minuteUtc).padStart(2, '0')} VE
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Días de corte automático</label>
            <div className="flex flex-wrap gap-2" data-testid="alliance-weekdays">
              {WEEKDAYS.map((d) => {
                const on = weekdays.has(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => setWeekdays((prev) => {
                      const next = new Set(prev);
                      if (next.has(d.v)) next.delete(d.v); else next.add(d.v);
                      return next;
                    })}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    data-testid={`alliance-weekday-${d.v}`}
                  >
                    {d.l}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {weekdays.size === 0 ? 'Ninguno seleccionado = corre TODOS los días.' : `Corre: ${fmtDays(Array.from(weekdays).sort((a, b) => a - b).join(','))}`}
            </p>
          </div>

          <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer border-gray-200" data-testid="alliance-fullbalance-toggle">
            <input type="checkbox" checked={fullBalance} onChange={(e) => setFullBalance(e.target.checked)} className="mt-1" data-testid="alliance-fullbalance-checkbox" />
            <div className="text-sm">
              <div className="font-semibold">Barrer todo el saldo acumulado</div>
              <div className="text-xs text-gray-600 mt-0.5">Cada corte dispersa el SALDO TOTAL del ledger (no una ventana de 24h). Recomendado cuando el corte no corre todos los días: el primer corte de la semana barre el backlog de los días sin corte.</div>
            </div>
          </label>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">Dirección del flujo bancario</label>
            <div className="grid grid-cols-1 gap-2">
              <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${flowDirection === 'merchant_to_caura' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}>
                <input type="radio" name="flow" value="merchant_to_caura" checked={flowDirection === 'merchant_to_caura'} onChange={(e) => setFlowDirection(e.target.value)} className="mt-1" data-testid="flow-merchant-to-caura" />
                <div className="text-sm">
                  <div className="font-semibold">Merchant → Caura <span className="text-xs text-gray-500">(ej. Lotería de Falcón)</span></div>
                  <div className="text-xs text-gray-600 mt-0.5">Los pagos P2P llegan a la cuenta del merchant. Caura DEBITA la comisión diariamente desde la cuenta del merchant a la cuenta de comisiones de Caura.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${flowDirection === 'caura_to_merchant' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}>
                <input type="radio" name="flow" value="caura_to_merchant" checked={flowDirection === 'caura_to_merchant'} onChange={(e) => setFlowDirection(e.target.value)} className="mt-1" data-testid="flow-caura-to-merchant" />
                <div className="text-sm">
                  <div className="font-semibold">Caura → Merchant <span className="text-xs text-gray-500">(ej. Ragu Market)</span></div>
                  <div className="text-xs text-gray-600 mt-0.5">Los pagos P2P llegan a la cuenta concentradora de Caura. Caura PAGA el neto al merchant diariamente (gross − fees banco − refunds − comisión).</div>
                </div>
              </label>
            </div>
          </div>

          <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{flowDirection === 'caura_to_merchant' ? 'Cuenta bancaria del merchant (destino del CCE diario)' : 'Cuenta bancaria del merchant (origen del CCE diario)'}</div>
            <div>
              <label className="block text-xs text-gray-700 mb-1">Cuenta operativa (20 dígitos)</label>
              <input type="text" value={bankAccount} onChange={(e) => setBankAccount(e.target.value.replace(/\D/g, '').slice(0, 20))} placeholder="01380010330100324070" maxLength={20} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="alliance-account-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-700 mb-1">RIF</label>
                <input type="text" value={bankRif} onChange={(e) => setBankRif(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="J123456789" className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="alliance-rif-input" />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Cód. banco</label>
                <input type="text" value={bankCode} onChange={(e) => setBankCode(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="0138" maxLength={4} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" data-testid="alliance-bankcode-input" />
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Nombre</label>
                <input type="text" value={bankName} onChange={(e) => setBankName(e.target.value.toUpperCase().slice(0, 80))} placeholder="RAGU MARKET CA" maxLength={80} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="alliance-name-input" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancelar</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50" data-testid="alliance-save-btn">
              {saving ? 'Guardando…' : 'Guardar alianza'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal de corte manual: muestra el PREVIEW REAL antes de mover dinero ──
// El botón viejo usaba un window.confirm con texto fijo ("últimas 24h") y
// nunca mandaba `full_balance`, así que para comercios configurados en modo
// "barrer todo el saldo" (ej. Lotería) calculaba la comisión sobre una ventana
// de 24h y dejaba el backlog atascado. Ahora el modo lo decide la config del
// comercio y los montos se simulan con el mismo motor del corte real.
function RunSettlementModal({ merchant, onClose, onDone }) {
  const merchantId = merchant.user_id || merchant.merchant_id;
  const fullBalance = !!merchant.alliance_settlement_full_balance;
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const res = await axios.get(`${API}/ledger/${merchantId}/settlement-preview`, {
          headers: headers(),
          params: fullBalance ? { full_balance: true } : {},
        });
        if (alive) setPreview(res.data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || 'No se pudo simular el corte');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [merchantId, fullBalance]);

  const expected = preview ? Number(preview.commission_amount || 0).toFixed(2) : '';
  const canExecute = !!preview && !executing && confirmText.trim() === expected && Number(expected) > 0 && !preview.sweep_in_progress;

  const execute = async () => {
    setExecuting(true); setResult(null);
    try {
      const body = fullBalance
        ? { merchant_id: merchantId, full_balance: true }
        : { merchant_id: merchantId, window_start: preview.window_start, window_end: preview.window_end };
      const res = await axios.post(`${API}/alliance-settlements/run-now`, body, { headers: headers() });
      const r = res.data?.result || res.data || {};
      setResult({
        ok: r.ok === true,
        data: r,
        error: r.ok === true ? null : (r.error || r.bank_transfer_error || 'El corte no se completó'),
      });
      await onDone?.();
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || 'Error ejecutando el corte' });
    } finally {
      setExecuting(false);
    }
  };

  const Row = ({ label, value, strong, color }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-600">{label}</span>
      <span className={`text-sm font-mono ${strong ? 'font-bold' : ''} ${color || 'text-gray-900'}`}>{value}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="run-settlement-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">Ejecutar corte ahora</h3>
            <p className="text-xs text-gray-500">{merchant.company_name || merchant.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="run-settlement-close">×</button>
        </div>

        <div className="p-6 space-y-4">
          <div className={`px-3 py-2 rounded-lg text-xs font-semibold ${fullBalance ? 'bg-purple-50 text-purple-800 border border-purple-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`} data-testid="run-settlement-mode">
            {fullBalance
              ? 'Modo: BARRIDO DE SALDO COMPLETO — liquida todo el saldo acumulado del ledger (backlog incluido) y lo deja en 0.'
              : 'Modo: VENTANA — liquida solo el neto de la ventana calculada; el saldo posterior queda para el próximo corte.'}
          </div>

          {loading && <p className="text-sm text-gray-500" data-testid="run-settlement-loading">Simulando el corte…</p>}
          {preview?.sweep_in_progress && (
            <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900" data-testid="run-settlement-sweep-in-progress">
              <strong>Hay un barrido EN CURSO</strong> para este comercio (corte {String(preview.sweep_settlement_id || '').slice(0, 8)}, iniciado {preview.sweep_started_at}).
              El botón queda bloqueado hasta que termine: ejecutar otro ahora barre dos veces el mismo dinero.
            </div>
          )}
          {err && <p className="text-sm text-red-600" data-testid="run-settlement-error">{err}</p>}

          {preview && (
            <>
              <div className="border rounded-lg px-4 py-3 bg-gray-50" data-testid="run-settlement-preview">
                <Row label="Saldo actual del ledger" value={fmtVES(preview.current_ledger_balance)} strong />
                {!fullBalance && (
                  <>
                    <Row label="Ventana" value={`${fmtDate(preview.window_start)} → ${fmtDate(preview.window_end)}`} />
                    <Row label="Ingresos brutos de la ventana" value={fmtVES(preview.gross_income)} />
                    <Row label="Fees del banco" value={`− ${fmtVES(preview.bank_commissions)}`} />
                    <Row label="Refunds" value={`− ${fmtVES(preview.refunds)}`} />
                  </>
                )}
                <Row label={fullBalance ? 'Base del corte (saldo)' : 'Neto de la ventana'} value={fmtVES(preview.net_income)} strong />
                {!fullBalance && (
                  <Row
                    label={preview.commission_basis === 'window_gross' ? 'Base comisión (bruto − refunds)' : 'Base comisión (neto)'}
                    value={fmtVES(preview.commission_base_amount ?? preview.net_income)}
                    strong
                  />
                )}
                <Row label={`Comisión Caura (${preview.rate_pct}% ${preview.commission_basis === 'window_gross' ? 'del bruto' : 'del neto'})`} value={fmtVES(preview.commission_amount)} strong color="text-green-700" />
                <Row label="Barrido al comercio" value={fmtVES(preview.sweep_estimate)} strong color="text-purple-700" />
                <Row label="Ledger después del corte" value={fmtVES(preview.ledger_after_estimate)} />
              </div>

              {merchant.commission_split_configured && merchant.commission_split_paused && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="run-settlement-split-warning">
                  El <strong>reparto de comisiones está APAGADO</strong>: los {fmtVES(preview.commission_amount)} quedarán en la cuenta
                  de comisiones de Caura sin repartirse entre los beneficiarios.
                </div>
              )}
              {preview.settlement_paused && (
                <div className="px-3 py-2 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-900" data-testid="run-settlement-paused-warning">
                  El corte automático de este comercio está <strong>pausado</strong>. Esta corrida manual sí se ejecuta.
                </div>
              )}
              {preview.sweep_capped && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                  El barrido se ajustó al saldo real disponible tras cobrar la comisión.
                </div>
              )}

              <div className="px-3 py-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-xs text-red-900 font-semibold mb-2">
                  Esto MUEVE DINERO REAL y no se puede deshacer. Escribe el monto exacto de la comisión
                  (<span className="font-mono">{expected}</span>) para habilitar el botón.
                </p>
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expected}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                  data-testid="run-settlement-confirm-input"
                />
              </div>

              {result && (
                <div className={`px-3 py-2 rounded-lg text-xs ${result.ok ? 'bg-green-50 border border-green-200 text-green-900' : 'bg-red-50 border border-red-200 text-red-900'}`} data-testid="run-settlement-result">
                  {result.ok
                    ? `Corte ejecutado. Comisión ${fmtVES(result.data?.commission_amount)} · ref ${result.data?.bank_transfer_reference || '—'} · barrido ${result.data?.sweep_status || '—'}`
                    : result.error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm" data-testid="run-settlement-cancel">Cerrar</button>
          <button
            onClick={execute}
            disabled={!canExecute}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-40"
            data-testid="run-settlement-execute"
          >
            {executing ? 'Ejecutando…' : 'Ejecutar corte real'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de reparto manual de comisión (10/45/45) ───────────────
// El reparto automático se programa 10 min después de cada corte y puede estar
// pausado por comercio; cuando lo está, la comisión se queda en la cuenta de
// Caura. Este modal permite repartirla a mano con el mismo desglose.
function CommissionSplitModal({ merchant, onClose }) {
  const merchantId = merchant.user_id || merchant.merchant_id;
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [selected, setSelected] = useState([]);
  const [legs, setLegs] = useState([]);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/commission-splits/preview`, {
        headers: headers(), params: { merchant_id: merchantId },
      });
      setData(res.data);
      setLegs([]);
      setSelected([]);
      setAmount('');
      setConfirmText('');
    } catch (e) {
      setErr(e.response?.data?.detail || 'No se pudo cargar el reparto');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [merchantId]);

  const recompute = async (value) => {
    setAmount(value);
    setConfirmText('');
    const n = Number(value);
    if (!n || n <= 0) { setLegs([]); return; }
    try {
      const res = await axios.get(`${API}/commission-splits/preview`, {
        headers: headers(), params: { merchant_id: merchantId, amount: n },
      });
      setLegs(res.data.legs || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Monto inválido');
    }
  };

  const toggle = (s) => {
    const next = selected.includes(s.settlement_id)
      ? selected.filter((x) => x !== s.settlement_id)
      : [...selected, s.settlement_id];
    setSelected(next);
    const total = (data?.unsplit_settlements || [])
      .filter((x) => next.includes(x.settlement_id))
      .reduce((acc, x) => acc + Number(x.commission_amount), 0);
    recompute(total > 0 ? total.toFixed(2) : '');
  };

  const markExternal = async () => {
    if (!selected.length) return;
    setExecuting(true);
    try {
      await axios.post(`${API}/commission-splits/mark-external`,
        { merchant_id: merchantId, settlement_ids: selected, note: 'Repartido fuera del sistema' },
        { headers: headers() });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'No se pudo marcar');
    } finally {
      setExecuting(false);
    }
  };

  const expected = Number(amount) > 0 ? Number(amount).toFixed(2) : '';
  const canExecute = !executing && legs.length > 0 && confirmText.trim() === expected;

  const execute = async () => {
    setExecuting(true); setResult(null);
    try {
      const res = await axios.post(`${API}/commission-splits/manual`,
        { merchant_id: merchantId, amount: Number(amount), settlement_ids: selected },
        { headers: headers() });
      setResult(res.data);
      setLegs(res.data.legs || legs);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Error ejecutando el reparto');
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="commission-split-modal">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">Repartir comisión ahora</h3>
            <p className="text-xs text-gray-500">{merchant.company_name || merchant.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="commission-split-close">×</button>
        </div>

        <div className="p-6 space-y-4">
          {loading && <p className="text-sm text-gray-500">Cargando beneficiarios…</p>}
          {err && <p className="text-sm text-red-600" data-testid="commission-split-error">{err}</p>}

          {data && (
            <>
              <div className="text-xs text-gray-600">
                Origen: <span className="font-mono">{data.source?.account}</span> · {data.source?.name}
              </div>

              {data.unsplit_settlements?.length > 0 && (
                <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900" data-testid="commission-split-unsplit">
                  <p className="mb-2">
                    Cortes de los últimos 30 días con comisión cobrada y <strong>sin registro de reparto</strong>.
                    Marca solo los que vas a repartir ahora: si ya repartiste alguno a mano, márcalo y usa
                    "Ya repartidos a mano" para que deje de aparecer.
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {data.unsplit_settlements.map((s) => (
                      <label key={s.settlement_id} className="flex items-center gap-2 cursor-pointer hover:bg-blue-100 rounded px-1 py-0.5">
                        <input
                          type="checkbox"
                          checked={selected.includes(s.settlement_id)}
                          onChange={() => toggle(s)}
                          data-testid={`commission-split-pick-${s.settlement_id}`}
                        />
                        <span className="font-mono text-[11px]">{fmtDate(s.created_at)}</span>
                        <span className="font-mono text-[11px] font-semibold ml-auto">{fmtVES(s.commission_amount)}</span>
                      </label>
                    ))}
                  </div>
                  {selected.length > 0 && (
                    <button
                      onClick={markExternal}
                      disabled={executing}
                      className="mt-2 text-[11px] px-2 py-1 border border-blue-300 rounded hover:bg-blue-100 disabled:opacity-40"
                      data-testid="commission-split-mark-external"
                    >
                      Marcar los {selected.length} seleccionados como "ya repartidos a mano"
                    </button>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Monto de comisión a repartir (VES)</label>
                <input
                  value={amount}
                  onChange={(e) => recompute(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                  placeholder="0.00"
                  data-testid="commission-split-amount"
                />
              </div>

              <div className="border rounded-lg divide-y" data-testid="commission-split-legs">
                {legs.map((l) => (
                  <div key={l.account} className="px-4 py-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{l.name} <span className="text-xs text-gray-500">({l.pct}%)</span></p>
                      <p className="text-[11px] font-mono text-gray-500">{l.account} · {l.rif}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono font-semibold">{fmtVES(l.amount)}</p>
                      {l.status && l.status !== 'pending' && (
                        <p className={`text-[11px] ${l.status === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                          {l.status === 'success' ? `ref ${l.reference || '—'}` : l.error}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {legs.length === 0 && <p className="px-4 py-3 text-xs text-gray-500">Indica un monto para ver el desglose.</p>}
              </div>

              <div className="px-3 py-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-xs text-red-900 font-semibold mb-2">
                  Esto envía {legs.length} transferencias reales. Escribe el monto exacto
                  (<span className="font-mono">{expected}</span>) para habilitar el botón.
                </p>
                <input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={expected}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                  data-testid="commission-split-confirm-input"
                />
              </div>

              {result && (
                <div className={`px-3 py-2 rounded-lg text-xs ${result.ok ? 'bg-green-50 border border-green-200 text-green-900' : 'bg-amber-50 border border-amber-200 text-amber-900'}`} data-testid="commission-split-result">
                  Reparto {result.status} — {result.ok_count}/{result.total_legs} transferencias enviadas.
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm" data-testid="commission-split-cancel">Cerrar</button>
          <button
            onClick={execute}
            disabled={!canExecute}
            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-40"
            data-testid="commission-split-execute"
          >
            {executing ? 'Enviando…' : 'Ejecutar reparto'}
          </button>
        </div>
      </div>
    </div>
  );
}
